/**
 * Step 7 — Cybersecurity Goals & Claims.
 *
 * Outer-tab layout (Story 14.A): the two collections + their
 * hierarchical Current/Child split are all rendered as flat outer
 * tabs via TableTabNav.
 *
 *   non-hierarchical: [Goals (N)] [Claims (N)]
 *   hierarchical:     [Goals · Current TARA (N)] [Goals · Child TARAs (M)]
 *                     [Claims · Current TARA (N)] [Claims · Child TARAs (M)]
 *
 * Two delete confirms + two form modals (one per collection) since
 * goal_id and claim_id are independent record-key namespaces.
 */
import React, { useMemo, useState } from 'react';
import { LoadingState, FailedState } from '../../../components/ui/LoadingState';

import { useConfirm } from '../../../components/ui/AlertDialog';
import { Icon } from '../../../components/ui/Icon';
import TableTabNav from '../components/TableTabNav/TableTabNav';
import { useStepSection, resolveSectionKey } from '../hooks/useStepSection';
import GoalsTable from './step7-goals/GoalsTable';
import GoalFormModal from './step7-goals/GoalFormModal';
import ClaimsTable from './step7-goals/ClaimsTable';
import ClaimFormModal from './step7-goals/ClaimFormModal';
import ChildGoalsTable, { type ChildGoalView } from './step7-goals/ChildGoalsTable';
import ChildClaimsTable, { type ChildClaimView } from './step7-goals/ChildClaimsTable';
import type { GoalRecord } from './step7-goals/goalsFieldSchema';
import type { ClaimRecord } from './step7-goals/claimsFieldSchema';
import {
  exportGoalsToCsv,
  exportClaimsToCsv,
  parseGoalsFile,
  parseClaimsFile,
} from './step7-goals/goalsClaimsCsv';
import type { Step7BodyProps } from './stepBodyProps';
import { notify } from '../../../services/notificationBus';
import styles from './Step7CybersecurityGoals.module.css';

// LoadingState/FailedState are JSX primitives; cast for use from TSX
// (mirrors the ButtonAny convention across the step-components).
const LoadingStateAny = LoadingState as unknown as React.ComponentType<any>;
const FailedStateAny = FailedState as unknown as React.ComponentType<any>;

const TAB_GOALS = 'goals';
const TAB_GOALS_CURRENT = 'goals:current';
const TAB_GOALS_CHILD = 'goals:child';
const TAB_CLAIMS = 'claims';
const TAB_CLAIMS_CURRENT = 'claims:current';
const TAB_CLAIMS_CHILD = 'claims:child';

type GoalModalState =
  | null
  | { kind: 'add' }
  | { kind: 'edit'; goal: GoalRecord };

type ClaimModalState =
  | null
  | { kind: 'add' }
  | { kind: 'edit'; claim: ClaimRecord };

export default function Step7CybersecurityGoals({
  status,
  editable,
  structured,
  goals,
  claims,
  crud,
  riskOptions,
  goalsHierarchical,
  claimsHierarchical,
  errorMessage,
}: Step7BodyProps) {
  const [goalModal, setGoalModal] = useState<GoalModalState>(null);
  const { confirm, confirmDialog } = useConfirm();
  const [claimModal, setClaimModal] = useState<ClaimModalState>(null);
  // Outer tab — default to the first goals tab. Updated below once
  // we know whether hierarchical mode applies (the keys differ).
  const [activeTab, setActiveTab] = useStepSection(TAB_GOALS);

  // NOTE: hooks must be called unconditionally. All useMemo calls live
  // above the early returns below so the hook count stays stable as
  // status flips between generating / draft / failed.
  const goalsTyped = goals as GoalRecord[];
  const claimsTyped = claims as ClaimRecord[];

  const childGoalsRaw: ChildGoalView[] = goalsHierarchical.enabled
    ? goalsTyped.filter((g: any) => g?.source === 'child_tara')
    : [];
  const childClaimsRaw: ChildClaimView[] = claimsHierarchical.enabled
    ? claimsTyped.filter((c: any) => c?.source === 'child_tara')
    : [];

  const childGoalView: ChildGoalView[] = useMemo(
    () =>
      childGoalsRaw.map((c) => ({
        ...c,
        is_relevant:
          goalsHierarchical.modifications[c.goal_id]?.is_relevant ??
          c.is_relevant ??
          true,
        relevance_justification:
          goalsHierarchical.modifications[c.goal_id]?.relevance_justification ??
          c.relevance_justification ??
          '',
      })),
    [childGoalsRaw, goalsHierarchical.modifications],
  );
  const childClaimView: ChildClaimView[] = useMemo(
    () =>
      childClaimsRaw.map((c) => ({
        ...c,
        is_relevant:
          claimsHierarchical.modifications[c.claim_id]?.is_relevant ??
          c.is_relevant ??
          true,
        relevance_justification:
          claimsHierarchical.modifications[c.claim_id]?.relevance_justification ??
          c.relevance_justification ??
          '',
      })),
    [childClaimsRaw, claimsHierarchical.modifications],
  );

  if (status === 'failed') {
    return (
      <FailedStateAny
        variant="page"
        title="Cybersecurity goals generation failed"
        description={errorMessage ?? structured.error ?? 'The cybersecurity goals agent encountered an error. Revise and retry.'}
      />
    );
  }

  const noDataYet = goalsTyped.length === 0 && claimsTyped.length === 0 && !structured.data;
  if (
    status === 'empty' ||
    status === 'generating' ||
    status === 'revising' ||
    (structured.loading && noDataYet)
  ) {
    return (
      <LoadingStateAny
        variant="page"
        title="Generating cybersecurity goals + claims"
        description="The agent is deriving goals (Reduce) + claims (Share/Retain) per ISO 21434 Clause 9.4. This usually takes 1–2 minutes."
      />
    );
  }

  /* ----- Goals handlers ----- */

  const handleGoalSave = async (values: Record<string, unknown>) => {
    try {
      if (goalModal?.kind === 'edit') {
        await crud.update(goalModal.goal.goal_id, values);
        notify({ type: 'success', message: `Goal "${values.goal_id}" updated.` });
      } else {
        await crud.add(values);
        notify({ type: 'success', message: `Goal "${values.goal_id}" added.` });
      }
      setGoalModal(null);
      structured.refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to save goal';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to save goal');
    }
  };

  const handleGoalDelete = async (target: GoalRecord) => {
    const ok = await confirm({
      title: 'Delete cybersecurity goal?',
      description: `Delete goal "${target.goal_id}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      media: <Icon name="delete" />,
    });
    if (!ok) return;
    try {
      await crud.remove(target.goal_id);
      notify({ type: 'success', message: `Goal "${target.goal_id}" deleted.` });
      structured.refetch();
    } catch (e: any) {
      notify({ type: 'error', title: 'Delete failed', message: e?.message || 'Failed to delete goal' });
    }
  };

  const handleGoalsExport = () => {
    exportGoalsToCsv(
      goalsHierarchical.enabled
        ? goalsTyped.filter((g: any) => g?.source !== 'child_tara')
        : goalsTyped,
    );
  };

  const handleGoalsImport = async (file: File) => {
    let records: GoalRecord[];
    try { records = await parseGoalsFile(file); } catch (e: any) {
      notify({ type: 'error', title: 'Import failed', message: e?.message || `Could not parse ${file.name}` });
      return;
    }
    if (records.length === 0) {
      notify({ type: 'warning', message: `${file.name} contained no goal rows.` });
      return;
    }
    const existingIds = new Set(goalsTyped.map((g) => g.goal_id));
    let added = 0, skipped = 0, failed = 0;
    for (const record of records) {
      if (!record?.goal_id) { failed += 1; continue; }
      if (existingIds.has(record.goal_id)) { skipped += 1; continue; }
      try {
        await crud.add(record as Record<string, unknown>);
        existingIds.add(record.goal_id);
        added += 1;
      } catch { failed += 1; }
    }
    if (added > 0) structured.refetch();
    const parts: string[] = [];
    if (added) parts.push(`${added} added`);
    if (skipped) parts.push(`${skipped} skipped (duplicate id)`);
    if (failed) parts.push(`${failed} failed`);
    notify({
      type: failed && !added ? 'error' : 'success',
      title: failed && !added ? 'Import failed' : 'Import complete',
      message: parts.join(' · ') || 'No rows processed.',
    });
  };

  /* ----- Claims handlers (mirror goals) ----- */

  const handleClaimSave = async (values: Record<string, unknown>) => {
    try {
      if (claimModal?.kind === 'edit') {
        await crud.update(claimModal.claim.claim_id, values);
        notify({ type: 'success', message: `Claim "${values.claim_id}" updated.` });
      } else {
        await crud.add(values);
        notify({ type: 'success', message: `Claim "${values.claim_id}" added.` });
      }
      setClaimModal(null);
      structured.refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to save claim';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to save claim');
    }
  };

  const handleClaimDelete = async (target: ClaimRecord) => {
    const ok = await confirm({
      title: 'Delete cybersecurity claim?',
      description: `Delete claim "${target.claim_id}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      media: <Icon name="delete" />,
    });
    if (!ok) return;
    try {
      await crud.remove(target.claim_id);
      notify({ type: 'success', message: `Claim "${target.claim_id}" deleted.` });
      structured.refetch();
    } catch (e: any) {
      notify({ type: 'error', title: 'Delete failed', message: e?.message || 'Failed to delete claim' });
    }
  };

  const handleClaimsExport = () => {
    exportClaimsToCsv(
      claimsHierarchical.enabled
        ? claimsTyped.filter((c: any) => c?.source !== 'child_tara')
        : claimsTyped,
    );
  };

  const handleClaimsImport = async (file: File) => {
    let records: ClaimRecord[];
    try { records = await parseClaimsFile(file); } catch (e: any) {
      notify({ type: 'error', title: 'Import failed', message: e?.message || `Could not parse ${file.name}` });
      return;
    }
    if (records.length === 0) {
      notify({ type: 'warning', message: `${file.name} contained no claim rows.` });
      return;
    }
    const existingIds = new Set(claimsTyped.map((c) => c.claim_id));
    let added = 0, skipped = 0, failed = 0;
    for (const record of records) {
      if (!record?.claim_id) { failed += 1; continue; }
      if (existingIds.has(record.claim_id)) { skipped += 1; continue; }
      try {
        await crud.add(record as Record<string, unknown>);
        existingIds.add(record.claim_id);
        added += 1;
      } catch { failed += 1; }
    }
    if (added > 0) structured.refetch();
    const parts: string[] = [];
    if (added) parts.push(`${added} added`);
    if (skipped) parts.push(`${skipped} skipped (duplicate id)`);
    if (failed) parts.push(`${failed} failed`);
    notify({
      type: failed && !added ? 'error' : 'success',
      title: failed && !added ? 'Import failed' : 'Import complete',
      message: parts.join(' · ') || 'No rows processed.',
    });
  };

  /* ----- Hierarchical splits ----- */

  const newGoals = goalsHierarchical.enabled
    ? goalsTyped.filter((g: any) => g?.source !== 'child_tara')
    : goalsTyped;
  const newClaims = claimsHierarchical.enabled
    ? claimsTyped.filter((c: any) => c?.source !== 'child_tara')
    : claimsTyped;
  const childGoals = childGoalsRaw;
  const childClaims = childClaimsRaw;

  const handleGoalToggle = (goalId: string) => {
    const current = childGoalView.find((c) => c.goal_id === goalId);
    goalsHierarchical.setRelevance(goalId, !(current?.is_relevant !== false));
  };
  const handleClaimToggle = (claimId: string) => {
    const current = childClaimView.find((c) => c.claim_id === claimId);
    claimsHierarchical.setRelevance(claimId, !(current?.is_relevant !== false));
  };

  // Build the outer tab list. Hierarchical mode flattens the inner
  // Current/Child split into the outer strip; non-hierarchical uses
  // one tab per collection.
  const hierarchical = goalsHierarchical.enabled; // hook keeps both flags in sync
  const tabs = hierarchical
    ? [
        { key: TAB_GOALS_CURRENT, label: `Goals · Current TARA (${newGoals.length})` },
        { key: TAB_GOALS_CHILD, label: `Goals · Child TARAs (${childGoals.length})` },
        { key: TAB_CLAIMS_CURRENT, label: `Claims · Current TARA (${newClaims.length})` },
        { key: TAB_CLAIMS_CHILD, label: `Claims · Child TARAs (${childClaims.length})` },
      ]
    : [
        { key: TAB_GOALS, label: `Goals (${goalsTyped.length})` },
        { key: TAB_CLAIMS, label: `Claims (${claimsTyped.length})` },
      ];

  // Snap activeTab onto a valid key for the current mode. Avoids the
  // "user toggles into hier mode while activeTab=goals" stale-key bug.
  const activeKey = resolveSectionKey(tabs, activeTab);

  const childInputsDisabledGoals = goalsHierarchical.approving || !editable;
  const childInputsDisabledClaims = claimsHierarchical.approving || !editable;

  return (
    <div className={styles.bleed}>
      <TableTabNav
        tabs={tabs}
        activeKey={activeKey}
        onChange={setActiveTab}
        ariaLabel="Cybersecurity Goals & Claims tabs"
        bleed={false}
        sticky={false}
      />

      {(activeKey === TAB_GOALS || activeKey === TAB_GOALS_CURRENT) && (
        <GoalsTable
          goals={hierarchical ? newGoals : goalsTyped}
          editable={editable}
          onAdd={() => setGoalModal({ kind: 'add' })}
          onEdit={(goal) => setGoalModal({ kind: 'edit', goal })}
          onDelete={(goal) => handleGoalDelete(goal)}
          onExport={handleGoalsExport}
          onImport={handleGoalsImport}
          emptyMessage={hierarchical ? 'No new goals yet for this TARA.' : undefined}
        />
      )}

      {activeKey === TAB_GOALS_CHILD && (
        <ChildGoalsTable
          goals={childGoalView}
          onToggleRelevance={handleGoalToggle}
          onJustificationChange={goalsHierarchical.setJustification}
          disabled={childInputsDisabledGoals}
          onSave={goalsHierarchical.onApprove}
        />
      )}

      {(activeKey === TAB_CLAIMS || activeKey === TAB_CLAIMS_CURRENT) && (
        <ClaimsTable
          claims={hierarchical ? newClaims : claimsTyped}
          editable={editable}
          onAdd={() => setClaimModal({ kind: 'add' })}
          onEdit={(claim) => setClaimModal({ kind: 'edit', claim })}
          onDelete={(claim) => handleClaimDelete(claim)}
          onExport={handleClaimsExport}
          onImport={handleClaimsImport}
          emptyMessage={hierarchical ? 'No new claims yet for this TARA.' : undefined}
        />
      )}

      {activeKey === TAB_CLAIMS_CHILD && (
        <ChildClaimsTable
          claims={childClaimView}
          onToggleRelevance={handleClaimToggle}
          onJustificationChange={claimsHierarchical.setJustification}
          disabled={childInputsDisabledClaims}
          onSave={claimsHierarchical.onApprove}
        />
      )}

      {/* Modals */}
      {goalModal && (
        <GoalFormModal
          mode={goalModal.kind}
          initialGoal={goalModal.kind === 'edit' ? goalModal.goal : null}
          riskOptions={riskOptions}
          onSubmit={handleGoalSave}
          onClose={() => setGoalModal(null)}
        />
      )}
      {claimModal && (
        <ClaimFormModal
          mode={claimModal.kind}
          initialClaim={claimModal.kind === 'edit' ? claimModal.claim : null}
          riskOptions={riskOptions}
          onSubmit={handleClaimSave}
          onClose={() => setClaimModal(null)}
        />
      )}

      {/* Delete confirms — one hook drives both (only one open at a time) */}
      {confirmDialog}
    </div>
  );
}
